/**
 * AuthorsTableV2 — Authors list body, ported from the prototype's
 * `.screen-authors` table (cms-redesign-mockup.html ~7126+, styles
 * 2020–2076).
 *
 * Layout: LibTable with four columns:
 *   • Author — 44px circular avatar + name + italic author_title
 *   • Sessions — author_num_sessions in JetBrains Mono
 *   • Social Links — Facebook / Instagram / X icons (dashed disabled
 *     style when the URL is missing, link out when present)
 *   • Actions — hover-revealed Edit
 *
 * No drag-to-reorder (the API has no sort_order on authors). Search
 * is a small client-side filter over name + title.
 *
 * Data source: GET /authors (legacy `{objects, count}` envelope) via
 * the existing `fetchAuthors` action. Reuses the v1 reducer slice
 * so a flag flip doesn't trigger a refetch.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAuthors } from '../../../actions/authorsActions';
import {
  LibTable,
  IconButton,
  cn,
} from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

// Same SVG paths the prototype uses (line 7180+) — F, IG, X.
const FacebookSvg = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12a10 10 0 1 0-11.5 9.9v-7H8v-2.9h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.7-1.6 1.5V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z" />
  </svg>
);

const InstagramSvg = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <path d="M16 11.4A4 4 0 1 1 12.6 8a4 4 0 0 1 3.4 3.4z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const XSvg = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function initials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/* ─── Social pill cluster ──────────────────────────────────────────── */
/*
 *  Each platform is a 26px circular link sitting on cream. When the
 *  URL is empty/null we keep the slot visible but dashed + faded —
 *  reading "this author has no Twitter on file" is faster than
 *  spotting a missing icon. Mirrors prototype `.social-link.disabled`.
 */
function SocialIcon({ href, label, icon }) {
  const enabled = !!href;
  const base = {
    width: 26,
    height: 26,
    display: 'grid',
    placeItems: 'center',
    background: enabled ? '#FAF7F1' : 'transparent',
    border: enabled ? '1px solid #E8E0CF' : '1px dashed #E8E0CF',
    borderRadius: '50%',
    color: enabled ? '#9CA1A8' : '#9CA1A833',
    transition: 'all 140ms ease',
    textDecoration: 'none',
    opacity: enabled ? 1 : 0.25,
    pointerEvents: enabled ? 'auto' : 'none',
  };
  if (!enabled) {
    return (
      <span aria-label={`${label} (none)`} style={base}>{icon}</span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      style={base}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#5C7E72';
        e.currentTarget.style.borderColor = '#75A293';
        e.currentTarget.style.background = '#FFFFFF';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#9CA1A8';
        e.currentTarget.style.borderColor = '#E8E0CF';
        e.currentTarget.style.background = '#FAF7F1';
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
    </a>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function AuthorsTableV2() {
  const dispatch = useDispatch();
  const authors = useSelector((s) => s.authors.authors);

  useEffect(() => {
    dispatch(fetchAuthors());
  }, [dispatch]);

  // Search lives in the global ⌘K palette — no in-page search input to
  // avoid duplicating the affordance.
  const visible = Array.isArray(authors) ? authors : null;

  if (!Array.isArray(authors)) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading authors…
      </div>
    );
  }

  return (
    <div>
      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No authors yet — add one to get started.
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* author cell */}
              <col />
              {/* sessions */}
              <col style={{ width: '14%' }} />
              {/* social */}
              <col style={{ width: '22%' }} />
              {/* actions */}
              <col style={{ width: '16%' }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Author</LibTable.Th>
                <LibTable.NumTh>Sessions</LibTable.NumTh>
                <LibTable.CenterTh>Social Links</LibTable.CenterTh>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {visible.map((a) => (
                <LibTable.Row key={a.author_id}>
                  {/* Author cell — 44px circular avatar + name + title. */}
                  <LibTable.Td>
                    <div className="flex items-center min-w-0" style={{ gap: 12 }}>
                      {a.author_image ? (
                        <div
                          className="bg-cream border border-sand"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            backgroundImage: `url('${a.author_image}')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        // Initials fallback when no image is on file —
                        // navy Asap Condensed on cream, matching the
                        // prototype's `.author-avatar.fallback` recipe.
                        <div
                          className="grid place-items-center font-condensed font-bold text-navy bg-cream border border-sand"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            fontSize: 15,
                            flexShrink: 0,
                          }}
                        >
                          {initials(a.author_name)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <Link
                          to={`/authors/edit/${a.author_id}`}
                          className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
                          style={{ fontSize: 14, lineHeight: 1.25 }}
                          title={a.author_name || ''}
                        >
                          {a.author_name || 'Untitled'}
                        </Link>
                        {a.author_title && (
                          <div
                            className="text-text-muted italic truncate"
                            style={{ fontSize: 12, marginTop: 2 }}
                            title={a.author_title}
                          >
                            {a.author_title}
                          </div>
                        )}
                      </div>
                    </div>
                  </LibTable.Td>

                  <LibTable.NumTd muted={!Number(a.author_num_sessions)}>
                    {Number(a.author_num_sessions ?? 0)}
                  </LibTable.NumTd>

                  <LibTable.CenterTd>
                    <span
                      className="inline-flex items-center"
                      style={{ gap: 4 }}
                    >
                      <SocialIcon
                        href={a.author_facebook_url}
                        label="Facebook"
                        icon={FacebookSvg}
                      />
                      <SocialIcon
                        href={a.author_instagram_url}
                        label="Instagram"
                        icon={InstagramSvg}
                      />
                      <SocialIcon
                        href={a.author_twitter_url}
                        label="X / Twitter"
                        icon={XSvg}
                      />
                    </span>
                  </LibTable.CenterTd>

                  <LibTable.Td style={{ textAlign: 'right' }}>
                    <div className={cn(
                      'inline-flex opacity-0 transition-opacity duration-150',
                      'group-hover:opacity-100',
                    )}>
                      <Link
                        to={`/authors/edit/${a.author_id}`}
                        aria-label={`Edit ${a.author_name}`}
                      >
                        <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                      </Link>
                    </div>
                  </LibTable.Td>
                </LibTable.Row>
              ))}
            </tbody>
          </LibTable>
        </div>
      )}
    </div>
  );
}
